// 原生 WebSocket 适配层：对 App.tsx 暴露与 socket.io 同形的 emit/on/off + "connect" 事件，
// 让前端通信层从 socket.io-client 切换到 Cloudflare Worker 的裸 WS 协议时，业务代码几乎零改动。
//
// 协议（与 worker/protocol.ts 对齐）：
//   上行 { event, data?, ackId? }；应答 { ackId, ok, ... }；推送 { event, data }；心跳 字面量 "ping"/"pong"
//
// 与 socket.io 的差异由本层吸收：
//   - 房间码即 DO 实例：连接 URL 必须带 ?room=CODE。create 时本层随机生成码并处理碰撞重试；join 用用户输入的码。
//   - 原生 WS 无自动重连：本层指数退避重连，重连成功触发 "connect"，复用 App 的 rejoinAfterReconnect 静默回房。

const ROOM_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const ROOM_CODE_LEN = 4;
const ACK_TIMEOUT_MS = 8000;
const OPEN_TIMEOUT_MS = 10000;
const HEARTBEAT_MS = 25000;
const HEARTBEAT_TIMEOUT_MS = HEARTBEAT_MS * 2;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10000;
const KICK_CLOSE_CODE = 4000;

type Handler = (data?: any) => void;
type AckResponse = { ok: boolean; error?: string; codeTaken?: boolean; room?: any; playerId?: string; secret?: string };
export type SocketClientOptions = {
  heartbeatMs?: number;
  heartbeatTimeoutMs?: number;
};

function randomRoomCode(): string {
  const buffer = new Uint32Array(ROOM_CODE_LEN);
  crypto.getRandomValues(buffer);
  let code = "";
  for (let i = 0; i < ROOM_CODE_LEN; i += 1) code += ROOM_CODE_ALPHABET[buffer[i] % ROOM_CODE_ALPHABET.length];
  return code;
}

function wsEndpoint(roomCode: string): string {
  const configured = (import.meta.env.VITE_WS_URL as string | undefined)?.trim();
  const base = configured || `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}room=${encodeURIComponent(roomCode)}`;
}

export class SocketClient {
  private ws?: WebSocket;
  private roomCode = "";
  private ackSeq = 1;
  private pending = new Map<number, (resp: AckResponse) => void>();
  private handlers = new Map<string, Set<Handler>>();
  private opening?: Promise<boolean>;
  private openResolve?: (ok: boolean) => void;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private retries = 0;
  private intentional = false;
  private lastPingAt = 0;
  private lastPongAt = 0;
  private visibilityHandler?: () => void;
  private readonly heartbeatMs: number;
  private readonly heartbeatTimeoutMs: number;

  constructor(options: SocketClientOptions = {}) {
    this.heartbeatMs = options.heartbeatMs ?? HEARTBEAT_MS;
    this.heartbeatTimeoutMs = options.heartbeatTimeoutMs ?? HEARTBEAT_TIMEOUT_MS;
  }

  on(event: string, handler: Handler): void {
    const set = this.handlers.get(event) || new Set<Handler>();
    set.add(handler);
    this.handlers.set(event, set);
  }

  off(event: string, handler?: Handler): void {
    if (!handler) {
      this.handlers.delete(event);
      return;
    }
    this.handlers.get(event)?.delete(handler);
  }

  emit(event: string, data?: any, ack?: (resp: AckResponse) => void): void {
    if (event === "room:create") {
      void this.doCreate(data, ack);
      return;
    }
    if (event === "room:join") {
      void this.doJoin(data, ack);
      return;
    }
    if (event === "room:leave") {
      void this.doLeave(ack);
      return;
    }
    // room:action 等：在当前连接上发送（必要时自动重连）。
    void this.request(event, data).then((resp) => ack?.(resp));
  }

  private async doCreate(data: any, ack?: (resp: AckResponse) => void): Promise<void> {
    // 4 位随机码碰撞概率极低，仍按 codeTaken 重试若干次以稳妥。
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const ok = await this.ensureConnected(randomRoomCode());
      if (!ok) {
        this.hardClose();
        this.roomCode = "";
        ack?.({ ok: false, error: "连接失败，请稍后重试" });
        return;
      }
      const resp = await this.request("room:create", data);
      if (resp?.codeTaken) {
        this.hardClose();
        continue;
      }
      ack?.(resp);
      return;
    }
    ack?.({ ok: false, error: "创建房间失败，请重试" });
  }

  private async doJoin(data: any, ack?: (resp: AckResponse) => void): Promise<void> {
    const code = String(data?.code || "").trim().toUpperCase();
    if (!code) {
      ack?.({ ok: false, error: "请输入房间码" });
      return;
    }
    const ok = await this.ensureConnected(code);
    if (!ok) {
      this.hardClose();
      this.roomCode = "";
      ack?.({ ok: false, error: "连接失败，请检查房间码或网络" });
      return;
    }
    const resp = await this.request("room:join", data);
    ack?.(resp);
  }

  private async doLeave(ack?: (resp: AckResponse) => void): Promise<void> {
    const response = this.roomCode ? await this.request("room:leave") : { ok: true };
    ack?.(response);
    this.hardClose();
    this.roomCode = "";
  }

  private async request(event: string, data?: any): Promise<AckResponse> {
    if (!this.isOpen()) {
      if (!this.roomCode) return { ok: false, error: "尚未连接房间" };
      const ok = await this.ensureConnected(this.roomCode);
      if (!ok) return { ok: false, error: "连接失败" };
    }
    const ackId = this.ackSeq++;
    return new Promise<AckResponse>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(ackId);
        resolve({ ok: false, error: "请求超时" });
      }, ACK_TIMEOUT_MS);
      this.pending.set(ackId, (resp) => {
        clearTimeout(timer);
        resolve(resp);
      });
      try {
        this.ws!.send(JSON.stringify({ event, data, ackId }));
      } catch {
        clearTimeout(timer);
        this.pending.delete(ackId);
        resolve({ ok: false, error: "发送失败" });
      }
    });
  }

  private ensureConnected(code: string): Promise<boolean> {
    if (this.roomCode !== code) {
      this.hardClose();
      this.roomCode = code;
    }
    if (this.isOpen()) return Promise.resolve(true);
    if (this.opening) return this.opening;
    return this.connect();
  }

  private connect(): Promise<boolean> {
    this.intentional = false;
    this.clearReconnect();
    this.opening = new Promise<boolean>((resolve) => {
      this.openResolve = resolve;
    });
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsEndpoint(this.roomCode));
    } catch {
      this.resolveOpen(false);
      return Promise.resolve(false);
    }
    this.ws = ws;
    const openTimer = setTimeout(() => {
      if (this.ws !== ws || ws.readyState === WebSocket.OPEN) return;
      this.resolveOpen(false);
      this.failPending("连接超时");
      if (this.ws === ws) this.ws = undefined;
      try {
        ws.close();
      } catch {
        // 忽略
      }
    }, OPEN_TIMEOUT_MS);
    const clearOpenTimer = () => clearTimeout(openTimer);
    ws.onopen = () => {
      clearOpenTimer();
      this.retries = 0;
      this.resolveOpen(true);
      this.startHeartbeat();
      this.emitLocal("connect");
    };
    ws.onmessage = (event) => this.onMessage(event);
    ws.onerror = () => {
      if (ws.readyState !== WebSocket.OPEN) this.resolveOpen(false);
      // 紧随其后的 onclose 会处理重连
    };
    ws.onclose = (event) => {
      clearOpenTimer();
      this.stopHeartbeat();
      this.resolveOpen(false);
      this.failPending("连接已关闭");
      if (this.ws === ws) this.ws = undefined;
      if (event.code === KICK_CLOSE_CODE) {
        // 被踢出：不重连（App 收到 room:kicked 已重置会话）。
        this.roomCode = "";
        return;
      }
      if (!this.intentional && this.roomCode) {
        // 掉线（锁屏 / 弱网 / 服务端重启）：先告知 App 以便提示「重连中」，再指数退避重连。
        // 与 socket.io 的 "disconnect" 事件同名，使 App.tsx 的横幅逻辑两端通用。
        this.emitLocal("disconnect");
        this.scheduleReconnect();
      }
    };
    return this.opening;
  }

  private scheduleReconnect(): void {
    this.clearReconnect();
    const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.retries);
    const jitter = backoff * 0.3 * Math.random();
    this.retries += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.roomCode && !this.isOpen()) void this.connect();
    }, backoff + jitter);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private hardClose(): void {
    this.intentional = true;
    this.clearReconnect();
    this.stopHeartbeat();
    const ws = this.ws;
    this.ws = undefined;
    this.opening = undefined;
    this.openResolve = undefined;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      ws.onopen = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch {
        // 忽略
      }
    }
  }

  private onMessage(event: MessageEvent): void {
    const raw = typeof event.data === "string" ? event.data : "";
    if (!raw) return;
    // Any server frame proves the connection is alive. A literal pong is consumed here;
    // JSON acks/pushes continue through the normal dispatch path below.
    this.lastPongAt = Date.now();
    if (raw === "pong") return;
    let payload: any;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (payload.ackId != null) {
      const resolve = this.pending.get(payload.ackId);
      this.pending.delete(payload.ackId);
      resolve?.(payload);
      return;
    }
    if (payload.event) this.emitLocal(payload.event, payload.data);
  }

  private emitLocal(event: string, data?: any): void {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch {
        // 单个 handler 异常不影响其余
      }
    });
  }

  private resolveOpen(ok: boolean): void {
    this.openResolve?.(ok);
    this.opening = undefined;
    this.openResolve = undefined;
  }

  private failPending(error: string): void {
    this.pending.forEach((resolve) => resolve({ ok: false, error }));
    this.pending.clear();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastPingAt = 0;
    this.lastPongAt = Date.now();
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), this.heartbeatMs);
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") this.sendHeartbeat();
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
    this.sendHeartbeat();
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
    this.lastPingAt = 0;
    this.lastPongAt = 0;
  }

  private sendHeartbeat(): void {
    if (!this.isOpen()) return;
    const now = Date.now();
    if (this.lastPingAt > this.lastPongAt) {
      if (now - this.lastPingAt >= this.heartbeatTimeoutMs) {
        // readyState can remain OPEN after a phone sleeps or switches networks. Closing
        // the stale socket enters the existing onclose -> exponential reconnect path.
        try {
          this.ws?.close(4001, "heartbeat timeout");
        } catch {
          // 浏览器会在后续网络事件中触发 close；这里不抛出影响界面。
        }
      }
      return;
    }
    this.lastPingAt = now;
    try {
      this.ws!.send("ping");
    } catch {
      try {
        this.ws?.close(4001, "heartbeat send failed");
      } catch {
        // 忽略，等待浏览器触发 close。
      }
    }
  }

  private isOpen(): boolean {
    return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
  }
}

export function createSocketClient(): SocketClient {
  return new SocketClient();
}
